import { Shield, CheckCircle } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

export const MoneyBackGuarantee = ({ days = 30, compact = false, hover = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  useEffect(() => {
    if (isHovered && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 10, // Position above the trigger
        left: rect.left,
      });
    }
  }, [isHovered]);

  if (hover) {
    return (
      <>
        <div 
          ref={triggerRef}
          className="relative inline-block" 
          data-testid="money-back-guarantee-hover"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Trigger Label */}
          <div className="flex items-center gap-1.5 text-green-400 cursor-pointer border border-green-500/30 rounded-lg px-2.5 py-1.5 hover:bg-green-500/10 transition-colors">
            <Shield size={16} />
            <span className="text-sm font-medium">Гарантия {days} дней</span>
          </div>
        </div>
        
        {/* Tooltip via Portal */}
        {isHovered && createPortal(
          <div 
            className="fixed z-[9999] transition-all duration-200"
            style={{ 
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: 'translateY(-100%)'
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="bg-[#E5EADC] border border-teal-500/40 rounded-xl p-5 w-[340px] shadow-2xl mb-2">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <Shield size={24} className="text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">
                    Гарантия возврата {days} дней
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-3">
                    Если курс вам не подойдёт — вернём деньги в течение {days} дней без лишних вопросов. 
                    Условие: пройдено не более 25% материала.
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <div className="flex items-center gap-1.5 text-green-400">
                      <CheckCircle size={14} />
                      <span>Полный возврат</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-green-400">
                      <CheckCircle size={14} />
                      <span>Без вопросов</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-green-400">
                      <CheckCircle size={14} />
                      <span>Быстрая обработка</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-sm" data-testid="money-back-guarantee-compact">
        <Shield size={16} className="text-green-500" />
        <span>Гарантия возврата {days} дней</span>
      </div>
    );
  }

  return (
    <div 
      className="bg-gradient-to-r from-green-900/30 to-emerald-900/20 border border-green-500/30 rounded-xl p-5 mt-6"
      data-testid="money-back-guarantee"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
          <Shield size={24} className="text-green-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-lg mb-1">
            Гарантия возврата {days} дней
          </h3>
          <p className="text-gray-400 text-sm leading-relaxed mb-3">
            Если курс вам не подойдёт — вернём деньги в течение {days} дней без лишних вопросов. 
            Условие: пройдено не более 25% материала.
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <div className="flex items-center gap-1.5 text-green-400">
              <CheckCircle size={14} />
              <span>Полный возврат</span>
            </div>
            <div className="flex items-center gap-1.5 text-green-400">
              <CheckCircle size={14} />
              <span>Без вопросов</span>
            </div>
            <div className="flex items-center gap-1.5 text-green-400">
              <CheckCircle size={14} />
              <span>Быстрая обработка</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoneyBackGuarantee;
